import { AxiosError } from 'axios';

import axiosInstance from '../helpers/axios';
import { DEFAULT_ERROR_MESSAGE } from '../helpers/constants';
import { TOpsTrainerClass } from './opsTrainer';

/**
 * /api/ops/instructors — the identity map (one person, every spelling the
 * data uses for them) and the logins that hang off it — and /api/me/teaching,
 * the instructor's own door: their classes, the run sheet, attendance.
 */

// ── Staff side ─────────────────────────────────────────────────────────────

export type TInstructorLoginStatus = 'none' | 'active' | 'inactive';

export type TOpsInstructor = {
  id: string;
  displayName: string;
  variants: string[];
  email: string | null;
  active: boolean;
  userId: string | null;
  loginStatus: TInstructorLoginStatus;
  classCount: number;
  upcomingCount: number;
  assignmentCount: number;
  lastClass: string | null;
};

export type TOpsInstructorDetail = Omit<
  TOpsInstructor,
  'classCount' | 'upcomingCount' | 'assignmentCount' | 'lastClass'
> & { loginEmail: string | null };

export type TOpsUnmappedSpelling = {
  name: string;
  classCount: number;
  upcomingCount: number;
  assignmentCount: number;
  resource: boolean;
  suggestedFor: string | null;
};

export type TOpsInstructorList = {
  items: TOpsInstructor[];
  unmapped: TOpsUnmappedSpelling[];
};

export type TOpsInstructorSeedResult = {
  created: number;
  attached: number;
  total: number;
};

export type TOpsInstructorLoginResult = {
  id: string;
  userId: string;
  email: string;
  created: boolean;
  /** Returned once, never again — the audit row records only that one was issued. */
  temporaryPassword: string;
};

export const getOpsInstructors = async () =>
  (await axiosInstance.get<TOpsInstructorList>('ops/instructors')).data;

export const seedOpsInstructors = async () =>
  (await axiosInstance.post<TOpsInstructorSeedResult>('ops/instructors/seed'))
    .data;

export const createOpsInstructor = async (body: {
  displayName: string;
  variants?: string[];
  email?: string;
}) =>
  (await axiosInstance.post<TOpsInstructorDetail>('ops/instructors', body))
    .data;

export const getOpsInstructor = async (id: string) =>
  (await axiosInstance.get<TOpsInstructorDetail>(`ops/instructors/${id}`)).data;

export const updateOpsInstructor = async (
  id: string,
  body: {
    displayName?: string;
    variants?: string[];
    email?: string;
    active?: boolean;
  },
) =>
  (
    await axiosInstance.patch<TOpsInstructorDetail>(
      `ops/instructors/${id}`,
      body,
    )
  ).data;

export const issueOpsInstructorLogin = async (
  id: string,
  body: { email?: string; firstName?: string; lastName?: string },
) =>
  (
    await axiosInstance.post<TOpsInstructorLoginResult>(
      `ops/instructors/${id}/login`,
      body,
    )
  ).data;

export const deactivateOpsInstructorLogin = async (id: string) =>
  (
    await axiosInstance.post<{ id: string; deactivated: boolean }>(
      `ops/instructors/${id}/deactivate`,
    )
  ).data;

// ── Instructor side ────────────────────────────────────────────────────────

export type TTeachingInstructor = {
  id: string;
  displayName: string;
  variants: string[];
};

export type TMyTeaching = {
  instructor: TTeachingInstructor;
  upcoming: TOpsTrainerClass[];
  past: TOpsTrainerClass[];
};

export type TRosterStatus = 'Attended' | 'No Attend';

export type TTeachingSeat = {
  id: string;
  number: string | null;
  name: string | null;
  email: string | null;
  status: string | null;
};

/**
 * The class as the trainer view shows it; when the schedule window does not
 * cover it the API falls back to the bare record, so everything past the
 * identity fields is optional.
 */
export type TTeachingClass = Pick<
  TOpsTrainerClass,
  'id' | 'name' | 'start' | 'end' | 'status'
> &
  Partial<Omit<TOpsTrainerClass, 'id' | 'name' | 'start' | 'end' | 'status'>>;

/** What the instructor answered to the travel trigger, as kept on the class. */
export type TTravelPrefs = {
  noneRequired: boolean;
  homeAirport: string | null;
  airline: string | null;
  timing: string | null;
  notes: string | null;
  submittedAt: string;
};

export type TTeachingTravel = {
  /** Onsite class, or travel already raised on it. */
  applies: boolean;
  status: string | null;
  prefs: TTravelPrefs | null;
};

export type TTravelPrefsInput = {
  noneRequired?: boolean;
  homeAirport?: string;
  airline?: string;
  timing?: string;
  notes?: string;
};

export type TTeachingRunSheet = {
  /** The instructor's own post-class form: its link, and whether one is on record. */
  instructorSurvey: { link: string | null; filed: boolean };
  travel: TTeachingTravel;
  class: TTeachingClass;
  roster: TTeachingSeat[];
  counts: { booked: number; attended: number; noAttend: number };
};

export const getMyTeaching = async () =>
  (await axiosInstance.get<TMyTeaching>('me/teaching')).data;

export const getMyTeachingClass = async (classId: string) =>
  (await axiosInstance.get<TTeachingRunSheet>(`me/teaching/${classId}`)).data;

export const markMyTeachingAttendance = async (
  classId: string,
  body: { registrationId: string; status: TRosterStatus },
) =>
  (
    await axiosInstance.post<{ registrationId: string; status: TRosterStatus }>(
      `me/teaching/${classId}/attendance`,
      body,
    )
  ).data;

/** POST /api/me/teaching/:classId/travel — the answer to the travel trigger (T4.04). */
export const submitMyTravelPrefs = async (
  classId: string,
  body: TTravelPrefsInput,
) =>
  (
    await axiosInstance.post<{
      classId: string;
      travel: TTeachingTravel;
      statusBefore: string | null;
      financeCcMissing: boolean;
      sendStatus: string;
    }>(`me/teaching/${classId}/travel`, body)
  ).data;

// ── Away dates (unavailability) ────────────────────────────────────────────

/** A class booked inside an away block. */
export type TUnavailableClass = {
  id: string;
  name: string | null;
  start: string | null;
  end: string | null;
  status: string | null;
  type: string | null;
  instructor: string | null;
  accountName: string | null;
};

export type TUnavailabilityBlock = {
  id: string;
  from: string;
  to: string;
  note: string | null;
  createdBy: string;
  source?: 'portal' | 'outlook';
  createdAt: string;
  conflicts: TUnavailableClass[];
};

export type TUnavailability = {
  instructor: { id: string; displayName: string };
  items: TUnavailabilityBlock[];
};

export type TUnavailabilityInput = { from: string; to?: string; note?: string };

export type TInstructorConflict = {
  instructorId: string;
  displayName: string;
  block: { id: string; from: string; to: string; note: string | null };
  class: TUnavailableClass;
};

// the instructor's own
export const getMyUnavailability = async () =>
  (await axiosInstance.get<TUnavailability>('me/unavailability')).data;

export const addMyUnavailability = async (body: TUnavailabilityInput) =>
  (await axiosInstance.post<TUnavailabilityBlock>('me/unavailability', body))
    .data;

export const removeMyUnavailability = async (blockId: string) =>
  (
    await axiosInstance.delete<{ id: string; removed: boolean }>(
      `me/unavailability/${blockId}`,
    )
  ).data;

// staff side
export const getOpsInstructorUnavailability = async (id: string) =>
  (
    await axiosInstance.get<TUnavailability>(
      `ops/instructors/${id}/unavailability`,
    )
  ).data;

export const addOpsInstructorUnavailability = async (
  id: string,
  body: TUnavailabilityInput,
) =>
  (
    await axiosInstance.post<TUnavailabilityBlock>(
      `ops/instructors/${id}/unavailability`,
      body,
    )
  ).data;

export const removeOpsInstructorUnavailability = async (
  id: string,
  blockId: string,
) =>
  (
    await axiosInstance.delete<{ id: string; removed: boolean }>(
      `ops/instructors/${id}/unavailability/${blockId}`,
    )
  ).data;

export const getOpsInstructorConflicts = async () =>
  (
    await axiosInstance.get<{ items: TInstructorConflict[] }>(
      'ops/instructors/conflicts',
    )
  ).data.items;

// ── Errors ─────────────────────────────────────────────────────────────────

/** The HTTP status behind a failed request, if there is one. */
export const apiStatus = (err: unknown): number | undefined =>
  (err as AxiosError | undefined)?.response?.status;

/**
 * The message the API put in the body — these routes explain themselves
 * ("already belongs to …", "opens when the class starts") and the generic
 * code table would flatten that to the default.
 */
export const apiMessage = (err: unknown): string => {
  const data = (err as AxiosError<{ message?: string | string[] }> | undefined)
    ?.response?.data;
  const message = data?.message;
  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message) && message.length) return message.join('. ');
  return DEFAULT_ERROR_MESSAGE;
};
